// Hotel search. Same card, same tokens, same rhythm as the flight search page
// — the shape of the form differs because the inputs do:
//
//   From/To      → one Destination (a city + country pair, not an id)
//   Depart/Return→ Check-in / Check-out, with the night count derived
//   Travellers   → Rooms & guests, one occupancy per room (see RoomsPicker)

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { saveHotelSearch } from "../lib/hotelSearchCache";
import {
  addDays, fmtDate, nightsBetween, nightsLabel, occupancyLabel, ymd, ymdToDate,
} from "../lib/hotelFormat";
import {
  DEFAULT_HOTEL_SOURCE,
  type DestinationSuggestion,
  type HotelSearchResponse,
  type Occupancy,
} from "../lib/hotelTypes";
import { CalendarPicker } from "../components/CalendarPicker";
import { DestinationPicker } from "../components/DestinationPicker";
import { RoomsPicker } from "../components/RoomsPicker";
import { HotelSearchingLoader } from "../components/HotelSearchingLoader";

type ActiveBox = "destination" | "checkin" | "checkout" | "rooms" | null;

const DEFAULT_DESTINATION: DestinationSuggestion = {
  city_name: "Dubai",
  country_code: "AE",
  country_name: "United Arab Emirates",
  label: "Dubai, United Arab Emirates",
};

// Popular starts, so a partner can get to results in one click. Country codes
// are the supplier's, and the city names are its own spelling — a near-miss
// returns zero hotels rather than an error, so these are not free text.
const QUICK_DESTINATIONS: DestinationSuggestion[] = [
  { city_name: "Dubai", country_code: "AE", country_name: "United Arab Emirates", label: "Dubai" },
  { city_name: "Goa", country_code: "IN", country_name: "India", label: "Goa" },
  { city_name: "Bangkok", country_code: "TH", country_name: "Thailand", label: "Bangkok" },
  { city_name: "Singapore", country_code: "SG", country_name: "Singapore", label: "Singapore" },
  { city_name: "London", country_code: "GB", country_name: "United Kingdom", label: "London" },
];

export function HotelSearch() {
  const nav = useNavigate();
  const { me } = useAuth();
  const partnerCurrency = me?.partner?.currency || "INR";

  const [destination, setDestination] = useState<DestinationSuggestion>(DEFAULT_DESTINATION);
  const [checkIn, setCheckIn] = useState(() => ymd(addDays(new Date(), 30)));
  const [checkOut, setCheckOut] = useState(() => ymd(addDays(new Date(), 33)));
  const [occupancies, setOccupancies] = useState<Occupancy[]>([{ adults: 2 }]);
  const [refundableOnly, setRefundableOnly] = useState(false);
  const [breakfastOnly, setBreakfastOnly] = useState(false);
  const [fourPlusStars, setFourPlusStars] = useState(false);

  const [active, setActive] = useState<ActiveBox>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!cardRef.current?.contains(e.target as Node)) setActive(null);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Check-out must stay after check-in. Nudging it along rather than clearing it
  // means picking a later check-in never leaves the form in an invalid state.
  useEffect(() => {
    if (checkOut <= checkIn) setCheckOut(ymd(addDays(ymdToDate(checkIn), 1)));
  }, [checkIn, checkOut]);

  const nights = nightsBetween(checkIn, checkOut);
  const roomsLabel = occupancyLabel(occupancies);
  const canSubmit = !!destination?.city_name && !!destination?.country_code && nights >= 1;

  const search = useMutation({
    mutationFn: async () => {
      const form = {
        city_name: destination.city_name,
        country_code: destination.country_code,
        country_name: destination.country_name,
        check_in: checkIn,
        check_out: checkOut,
        occupancies,
        nationality: "IN",
        currency: partnerCurrency,
        source: DEFAULT_HOTEL_SOURCE,
      };
      const payload: Record<string, unknown> = {
        ...form,
        // The quick filters are pushed to the supplier where it supports them
        // (refundable, stars) and applied on the results page where it doesn't
        // (breakfast) — see HotelResults.
        ...(refundableOnly ? { refundable_only: true } : {}),
        ...(fourPlusStars ? { star_rating: [4, 5] } : {}),
        limit: 60,
      };
      delete payload.country_name;

      const resp = await api.post<HotelSearchResponse>("/v1/hotels/search/", payload);

      // "Nothing available on these dates" comes back as a 200 with an empty
      // list — a valid answer, not an error. Turn it into one here so the empty
      // state renders instead of navigating to a Results page with nothing on
      // it.
      if (!resp.hotels?.length) {
        throw new ApiError(200, resp, resp.detail || "No hotels found for these dates.");
      }

      saveHotelSearch({ form, response: resp, fetched_at: Date.now() });
      return resp;
    },
    onSuccess: () => nav(`/results${breakfastOnly ? "?breakfast=1" : ""}`),
  });

  const errMsg = search.error instanceof ApiError ? search.error.message : null;

  return (
    <>
      {search.isPending && (
        <HotelSearchingLoader
          city={destination.city_name}
          country={destination.country_name}
          checkIn={checkIn}
          checkOut={checkOut}
          nights={nights}
          roomsLabel={roomsLabel}
        />
      )}

      <section className="flights-hero">
        <div className="container">
          <div className="page-head">
            <div className="eyebrow">
              <Link to="/">Home</Link>
              <span className="crumb-sep">›</span>
              <b>Hotels</b>
            </div>
            <h1>Where are you <span className="serif">staying</span>?</h1>
            <p className="lede">
              Pick a city, your dates, and how many rooms. Kaira checks live
              availability and prices every room the property will actually sell.
            </p>
          </div>

          <div className="search-card" ref={cardRef}>
            <div className="search-head">
              <div className="search-tabs">
                <button type="button" className="search-tab active">Stays</button>
              </div>
              <div className="hotel-nights-chip">
                {nights >= 1 ? nightsLabel(nights) : "Pick your dates"}
              </div>
            </div>

            <div className="search-fields-row hotel-fields-row">
              <DestinationPicker
                label="Destination"
                value={destination}
                onChange={setDestination}
                isActive={active === "destination"}
                onActivate={() => setActive("destination")}
                onClose={() => setActive(null)}
              />

              <FieldBox
                isActive={active === "checkin"}
                onClick={() => setActive("checkin")}
                label="Check-in"
                value={fmtDate(checkIn)}
                sub={weekdayOf(checkIn)}
                popover={active === "checkin" ? (
                  <CalendarPicker
                    selected={ymdToDate(checkIn)}
                    minDate={new Date()}
                    onSelect={(d) => { setCheckIn(ymd(d)); setActive("checkout"); }}
                  />
                ) : null}
              />

              <FieldBox
                isActive={active === "checkout"}
                onClick={() => setActive("checkout")}
                label="Check-out"
                value={fmtDate(checkOut)}
                sub={weekdayOf(checkOut)}
                popover={active === "checkout" ? (
                  <CalendarPicker
                    selected={ymdToDate(checkOut)}
                    minDate={addDays(ymdToDate(checkIn), 1)}
                    onSelect={(d) => { setCheckOut(ymd(d)); setActive(null); }}
                  />
                ) : null}
              />

              <FieldBox
                isActive={active === "rooms"}
                onClick={() => setActive("rooms")}
                label="Rooms & guests"
                value={`${occupancies.length} ${occupancies.length === 1 ? "room" : "rooms"}`}
                sub={roomsLabel}
                popover={active === "rooms" ? (
                  <RoomsPicker occupancies={occupancies} onChange={setOccupancies} />
                ) : null}
              />
            </div>

            {errMsg && (
              <div className="empty-state" style={{ margin: "18px 0 0", padding: "20px 22px" }}>
                <span className="emoji">🏨</span>
                <h3>Nothing available for these <span className="serif">dates</span></h3>
                <p>The supplier had no bookable rooms for this search. The exact message:</p>
                <div className="api-msg">{errMsg}</div>
                <p style={{ fontSize: 12, marginTop: 0 }}>
                  Try different dates, fewer rooms, or a nearby city.
                </p>
              </div>
            )}

            <div className="search-foot">
              <div className="search-quickrow">
                <button
                  type="button"
                  className={`qpill${refundableOnly ? " on" : ""}`}
                  onClick={() => setRefundableOnly((v) => !v)}
                >
                  Free cancellation
                  {refundableOnly && <span className="qpill-state">on</span>}
                </button>
                <button
                  type="button"
                  className={`qpill${breakfastOnly ? " on" : ""}`}
                  onClick={() => setBreakfastOnly((v) => !v)}
                >
                  Breakfast included
                  {breakfastOnly && <span className="qpill-state">on</span>}
                </button>
                <button
                  type="button"
                  className={`qpill${fourPlusStars ? " on" : ""}`}
                  onClick={() => setFourPlusStars((v) => !v)}
                >
                  4★ &amp; up
                  {fourPlusStars && <span className="qpill-state">on</span>}
                </button>
                <button
                  type="button"
                  className={`qpill${occupancies.length > 1 ? " on" : ""}`}
                  onClick={() =>
                    setOccupancies((rooms) =>
                      rooms.length > 1 ? [rooms[0]] : [...rooms, { adults: 2 }])
                  }
                >
                  Add a room
                </button>
              </div>
              <button
                type="button"
                className={`search-cta${search.isPending ? " loading" : ""}`}
                disabled={!canSubmit || search.isPending}
                onClick={() => canSubmit && search.mutate()}
                style={{ opacity: canSubmit || search.isPending ? 1 : 0.55 }}
              >
                Search hotels
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          <div className="nudges">
            {QUICK_DESTINATIONS.map((d) => (
              <button
                key={`${d.city_name}-${d.country_code}`}
                type="button"
                className="nudge"
                onClick={() => setDestination({ ...d, label: `${d.city_name}, ${d.country_name}` })}
              >
                <span className="k-mini">K</span>
                stays in {d.city_name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="inspire-section">
        <div className="container">
          <div className="inspire-head">
            <div className="k-avatar" />
            <h2>Where Kaira's <span className="serif">booking</span> rooms this week.</h2>
          </div>
          <div className="inspire-grid">
            <InspireCard variant="dubai"  emoji="🏜️" place="Dubai · UAE"        name="Marina high-floors"     meta="free cancellation" amount="from ₹ 8,900" />
            <InspireCard variant="bali"   emoji="🌴" place="Bali · Indonesia"    name="Ubud pool villas"       meta="breakfast included" amount="from ₹ 6,400" />
            <InspireCard variant="tokyo"  emoji="⛩️" place="Tokyo · Japan"       name="Shinjuku compacts"      meta="walk to the metro"  amount="from ₹ 11,200" />
            <InspireCard variant="london" emoji="🌧️" place="London · UK"         name="Zone 1 boutiques"       meta="late check-out"     amount="from ₹ 18,700" />
          </div>
        </div>
      </section>
    </>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

function weekdayOf(value: string): string {
  const d = ymdToDate(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { weekday: "short" }) + " · " + d.getFullYear();
}

function FieldBox(props: {
  isActive: boolean;
  onClick: () => void;
  label: string;
  value: React.ReactNode;
  sub: React.ReactNode;
  popover?: React.ReactNode;
}) {
  return (
    <div
      className={`field-box${props.isActive ? " active" : ""}`}
      onClick={(e) => { e.stopPropagation(); props.onClick(); }}
    >
      <div className="label">{props.label}</div>
      <div className="value">{props.value}</div>
      <div className="sub">{props.sub}</div>
      {props.popover}
    </div>
  );
}

function InspireCard(props: {
  variant: "bali" | "tokyo" | "dubai" | "london";
  emoji: string; place: string; name: string; meta: string; amount: string;
}) {
  return (
    <article className="inspire-card">
      <div className={`inspire-img ${props.variant}`}>
        <span className="inspire-emoji">{props.emoji}</span>
      </div>
      <div className="inspire-body">
        <div className="route">{props.place}</div>
        <h4>{props.name}</h4>
        <div className="meta">
          <span>{props.meta}</span>
          <b>{props.amount}</b>
        </div>
      </div>
    </article>
  );
}
